import { useApi } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { KpiTile } from "@/components/common/KpiTile";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { SeverityChip } from "@/components/common/SeverityChip";
import { fmtCurrency, fmtNum, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Download } from "lucide-react";

export default function CostLedger() {
  const { data: tree = [], isLoading, error } = useApi("budget-tree", "/ledger/budget-tree");
  const { data: seats = [] } = useApi("seats", "/ledger/seats");
  if (error) return <ErrorState />;

  const totalBudget = tree.reduce((a, b) => a + b.budget_usd, 0);
  const totalSpent = tree.reduce((a, b) => a + b.spent_usd, 0);
  const idleCost = seats.reduce((a, s) => a + s.idle_cost_usd, 0);

  return (
    <div>
      <PageHeader eyebrow="Navitra Ledger" title="Cost Ledger" subtitle="Budget tree with burn and forecast, seat intelligence and chargeback — tokens, credits and seats in one unit.">
        <Button size="sm" variant="outline" onClick={() => toast.success("Chargeback export queued (CSV)")} data-testid="chargeback-export"><Download className="h-4 w-4 mr-1.5" />Chargeback export</Button>
      </PageHeader>

      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <KpiTile testid="budget" label="Monthly budget" value={fmtCurrency(totalBudget)} sub="across 8 business units" explain={{ formula: "Σ budget_usd across BU nodes", sources: [{ id: "Ledger", name: "Budget tree" }] }} />
        <KpiTile testid="spent" accent label="Spent to date" value={fmtCurrency(totalSpent)} sub={`${((totalSpent / totalBudget) * 100).toFixed(0)}% consumed`} explain={{ formula: "Σ spent_usd", sources: [{ id: "CP-08", name: "Provider usage" }] }} />
        <KpiTile testid="idle" label="Idle seat waste" value={fmtCurrency(idleCost)} sub="reclaimable per month" explain={{ formula: "Σ idle_seats × monthly_price", sources: [{ id: "CP-01", name: "Entra licences" }] }} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-display text-lg">Budget tree · burn & forecast</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? <LoadingBlock /> : (
              <Accordion type="multiple" className="w-full">
                {tree.map((bu) => {
                  const pct = Math.min(100, (bu.spent_usd / bu.budget_usd) * 100);
                  const over = bu.forecast_usd > bu.budget_usd;
                  return (
                    <AccordionItem key={bu.id} value={bu.id} data-testid={`bu-${bu.cost_centre}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex-1 pr-4">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-medium">{bu.name} <span className="mono text-xs text-muted-foreground ml-1">{bu.cost_centre}</span></span>
                            <span className="text-sm mono tabular-nums">{fmtCurrency(bu.spent_usd)} / {fmtCurrency(bu.budget_usd)}</span>
                          </div>
                          <Progress value={pct} className={over ? "[&>div]:bg-sev-warning" : "[&>div]:bg-navy"} />
                          {over && <p className="text-xs text-sev-warning mt-1.5 text-left">Forecast {fmtCurrency(bu.forecast_usd)} · breach {fmtDate(bu.breach_date)}</p>}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2 pl-2">{bu.children.map((c, i) => (
                          <div key={i} className="flex items-center justify-between text-sm border-l-2 border-muted pl-3 py-1"><span className="text-muted-foreground">{c.name}</span><span className="mono">{fmtCurrency(c.spent_usd)} / {fmtCurrency(c.budget_usd)}</span></div>
                        ))}</div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Seat intelligence</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Tool</TableHead><TableHead className="text-right">Idle</TableHead><TableHead className="text-right">Waste</TableHead></TableRow></TableHeader>
              <TableBody>
                {seats.map((s) => (
                  <TableRow key={s.tool}>
                    <TableCell><p className="font-medium text-sm">{s.tool}</p><p className="text-xs text-muted-foreground">{s.active}/{s.assigned} active</p></TableCell>
                    <TableCell className="text-right"><SeverityChip level={s.idle > s.assigned * 0.15 ? "warning" : "success"} label={fmtNum(s.idle)} /></TableCell>
                    <TableCell className="text-right mono">{fmtCurrency(s.idle_cost_usd)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button className="w-full mt-4" variant="outline" size="sm" onClick={() => toast.success("Reclaim task created for idle seats")} data-testid="reclaim-seats">Reclaim idle seats</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
